import { useEffect, useRef, useState } from 'react';
import { clientApi } from '@/config/axios';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { usePermissions } from '@/features/permissions/hooks/use-permissions';
import { DocumentViewerSheet } from '@/features/emr/components/DocumentViewerSheet';
import type { GeneratedDocumentOut } from '@/features/emr/api/documents-api';
import type { ClinicalEpisode, ClinicalEvent } from './types';
import type { PracticeConfiguration } from './rules';
import { eventHeads } from './projection';
import { OfflineTransferPreview } from './OfflineTransferPreview';

const kinds = {
  BIRTH_SUMMARY: 'Birth summary',
  TRANSFER_PACKET: 'Transfer packet',
  PRENATAL: 'Prenatal flowsheet',
  LABOR: 'Labor flowsheet',
  POSTPARTUM: 'Postpartum record',
  FULL_RECORD: 'Complete audit record',
};
type Kind = keyof typeof kinds;
interface Snapshot {
  source_hash: string;
  narrative: string;
  title: string;
  warnings: string[];
  encounter_ids: string[];
}
interface SavedReport {
  document_id: string;
  kind: Kind;
  title: string;
  signed_version_id: string | null;
  babies: { fetus_id: string; label: string }[];
}
export interface ReportWorkspaceProps {
  organizationId: string;
  episode: ClinicalEpisode;
  events: ClinicalEvent[];
  encounterId: string;
  timeZone: string;
  configuration?: PracticeConfiguration | null;
  offline?: boolean;
}
export function downloadClinicalFile(data: BlobPart, type: string, name: string) {
  const url = URL.createObjectURL(new Blob([data], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
function errorText(error: unknown) {
  const message = (error as { response?: { data?: { detail?: unknown; message?: unknown } } }).response?.data;
  return typeof message?.detail === 'string'
    ? message.detail
    : typeof message?.message === 'string'
      ? message.message
      : 'Could not complete this request. Your draft is retained; check the connection and retry.';
}
export function ReportWorkspace(props: ReportWorkspaceProps) {
  // Keep online-only hooks out of the cold offline entry point.
  return props.offline ? <OfflineTransferPreview {...props} /> : <OnlineReports {...props} />;
}
function OnlineReports({
  organizationId,
  episode,
  events,
  encounterId,
  timeZone,
  configuration,
}: ReportWorkspaceProps) {
  const { can } = usePermissions();
  const [kind, setKind] = useState<Kind>('BIRTH_SUMMARY');
  const [source, setSource] = useState('');
  const [preview, setPreview] = useState<Snapshot | null>(null);
  const [text, setText] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uncertainSave, setUncertainSave] = useState(false);
  const [message, setMessage] = useState('');
  const [reports, setReports] = useState<SavedReport[]>([]);
  const [ready, setReady] = useState<
    { kind: Kind; encounter_id: string; source_event_id: string | null; title: string }[]
  >([]);
  const [document, setDocument] = useState<GeneratedDocumentOut | null>(null);
  const receipt = useRef<{ body: string; id: string } | null>(null);
  const scope = `${organizationId}/${episode.id}/${encounterId}`;
  const generation = useRef(0);
  const base = `/api/v1/organizations/${organizationId}/flowsheets`;
  const selection = {
    kind,
    encounter_id: ['BIRTH_SUMMARY', 'TRANSFER_PACKET', 'LABOR'].includes(kind) ? encounterId : null,
    source_event_id: kind === 'TRANSFER_PACKET' ? source || null : null,
  };
  const transfers = eventHeads(events).filter(
    (e) =>
      e.encounter_id === encounterId &&
      e.event_type === 'TRANSPORT' &&
      e.status === 'ACTIVE' &&
      Date.parse(e.occurred_at) <= Date.now(),
  );
  useEffect(() => {
    generation.current++;
    setPreview(null);
    setText('');
    setReviewed(false);
    setSource('');
    setReports([]);
    setDocument(null);
    setUncertainSave(false);
    setBusy(false);
    receipt.current = null;
    setMessage('');
    const epoch = generation.current;
    if (can('read_document'))
      void clientApi
        .get(`${base}/episodes/${episode.id}/reports`)
        .then((r) => {
          if (epoch === generation.current) setReports(r.data);
        })
        .catch(() => {});
    return () => {
      generation.current++;
    };
  }, [scope]); // Scope changes discard state; ordinary evidence refresh preserves edits.
  useEffect(() => {
    let current = true;
    setReady([]);
    void clientApi
      .get(`${base}/episodes/${episode.id}/reports-readiness`)
      .then((r) => {
        if (current)
          setReady(
            (r.data.candidates ?? []).filter((item: { encounter_id: string }) => item.encounter_id === encounterId),
          );
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, [scope, events]);
  async function prepareCandidate(candidate: { kind: Kind; encounter_id: string; source_event_id: string | null }) {
    const epoch = generation.current;
    const result = await clientApi.post(`${base}/episodes/${episode.id}/report-preview`, candidate);
    if (epoch !== generation.current) return;
    setKind(candidate.kind);
    setSource(candidate.source_event_id ?? '');
    setPreview(result.data);
    setText(result.data.narrative);
    setReviewed(false);
    receipt.current = null;
  }
  function changeKind(value: Kind) {
    generation.current++;
    setKind(value);
    setPreview(null);
    setReviewed(false);
    setText('');
    receipt.current = null;
  }
  async function run(action: () => Promise<void>) {
    if (busy) return;
    const epoch = generation.current;
    setBusy(true);
    setMessage('');
    try {
      await action();
    } catch (error) {
      if (epoch === generation.current) setMessage(errorText(error));
    } finally {
      if (epoch === generation.current) setBusy(false);
    }
  }
  async function loadPreview() {
    const epoch = generation.current;
    const result = await clientApi.post(`${base}/episodes/${episode.id}/report-preview`, selection);
    if (epoch !== generation.current) return;
    setPreview(result.data);
    setText(result.data.narrative);
    setReviewed(false);
    receipt.current = null;
  }
  async function save() {
    if (!preview || !reviewed) return;
    const input = { ...selection, expected_hash: preview.source_hash, narrative: text };
    const body = JSON.stringify(input);
    if (receipt.current?.body !== body) receipt.current = { body, id: crypto.randomUUID() };
    const epoch = generation.current;
    setUncertainSave(true);
    let result;
    try {
      result = await clientApi.post(`${base}/episodes/${episode.id}/reports`, {
        ...input,
        request_id: receipt.current.id,
      });
    } catch (error) {
      const status = (error as { response?: { status?: number } }).response?.status;
      if (epoch === generation.current && status && status >= 400 && status < 500) setUncertainSave(false);
      throw error;
    }
    if (epoch !== generation.current) return;
    setUncertainSave(false);
    const [saved, documentResult] = await Promise.all([
      clientApi.get(`${base}/episodes/${episode.id}/reports`),
      clientApi.get(`/api/v1/organizations/${organizationId}/documents/${result.data.document_id}`),
    ]);
    if (epoch !== generation.current) return;
    setReports(saved.data);
    setMessage('Draft saved. Open it to review and sign.');
    setDocument(documentResult.data);
  }
  async function exportFile(format: 'pdf' | 'csv' | 'fhir') {
    if (!preview) return;
    const result = await clientApi.post(
      `${base}/episodes/${episode.id}/report-export`,
      { ...selection, expected_hash: preview.source_hash, format },
      { responseType: 'blob' },
    );
    downloadClinicalFile(
      result.data,
      format === 'pdf' ? 'application/pdf' : format === 'fhir' ? 'application/fhir+json' : 'text/csv;charset=utf-8',
      `${kind.toLowerCase()}-source.${format === 'fhir' ? 'json' : format}`,
    );
    setMessage('Downloaded the source record. Narrative edits are included only after saving and signing the report.');
  }
  return (
    <section
      className="flex flex-col gap-4 rounded-lg border p-4"
      aria-label="Clinical reports and exports"
      data-encounter-draft>
      <h3 className="text-lg font-semibold">Clinical reports and exports</h3>
      <p className="text-sm text-muted-foreground">
        Review recorded evidence, prepare a draft, then use the report signing workflow. Missing information stays
        marked as not recorded.
      </p>
      {!!ready.length && (
        <div className="flex flex-col gap-2">
          <p className="font-medium">Ready for review from recorded milestones</p>
          <div className="flex flex-wrap gap-2">
            {ready.map((candidate, i) => (
              <Button
                key={i}
                type="button"
                variant="outline"
                disabled={busy || uncertainSave || !!preview}
                onClick={() =>
                  void run(() =>
                    prepareCandidate({
                      kind: candidate.kind,
                      encounter_id: candidate.encounter_id,
                      source_event_id: candidate.source_event_id,
                    }),
                  )
                }>
                {candidate.title}
              </Button>
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-3">
        <label>
          Report
          <select
            className="block min-h-11 rounded border p-2"
            value={kind}
            onChange={(e) => changeKind(e.target.value as Kind)}
            disabled={busy || uncertainSave}>
            {Object.entries(kinds).map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {kind === 'TRANSFER_PACKET' && (
          <label>
            Transport entry
            <select
              aria-label="Transport entry"
              className="block min-h-11 rounded border p-2"
              value={source}
              disabled={busy || uncertainSave}
              onChange={(e) => {
                setSource(e.target.value);
                setPreview(null);
                setReviewed(false);
              }}>
              <option value="">Choose a confirmed entry</option>
              {transfers.map((e) => (
                <option key={e.id} value={e.id}>
                  {new Intl.DateTimeFormat(undefined, { dateStyle: 'short', timeStyle: 'short', timeZone }).format(
                    new Date(e.occurred_at),
                  )}
                </option>
              ))}
            </select>
          </label>
        )}
        <Button
          type="button"
          variant="outline"
          disabled={busy || uncertainSave || (kind === 'TRANSFER_PACKET' && !source)}
          onClick={() => void run(loadPreview)}>
          {preview ? 'Refresh from current evidence' : 'Prepare preview'}
        </Button>
      </div>
      {preview && (
        <>
          <h4 className="font-semibold">{preview.title}</h4>
          {preview.warnings.map((w, i) => (
            <p key={i} role="note">
              {w}
            </p>
          ))}
          <label className="block">
            Draft narrative
            <Textarea
              aria-label="Draft narrative"
              rows={16}
              value={text}
              disabled={busy || uncertainSave}
              onChange={(e) => {
                setText(e.target.value);
                setReviewed(false);
              }}
            />
          </label>
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={reviewed}
              disabled={busy || uncertainSave}
              onChange={(e) => setReviewed(e.target.checked)}
            />
            I reviewed this narrative against the source observations.
          </label>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              disabled={busy || !reviewed || !can('generate_document')}
              onClick={() => void run(save)}>
              Save report draft
            </Button>
            {can('export_document') && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void run(() => exportFile('pdf'))}>
                  Download source PDF
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void run(() => exportFile('csv'))}>
                  Download source CSV
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void run(() => exportFile('fhir'))}>
                  Download FHIR R4
                </Button>
                <p className="text-sm text-muted-foreground">
                  FHIR exports are a source-evidence collection for integration testing; receiving-system validation is
                  required.
                </p>
              </>
            )}
          </div>
        </>
      )}
      {uncertainSave && (
        <p role="alert">
          The save result is not confirmed. Retry Save report draft before editing or leaving this encounter; the same
          request identity will be reused.
        </p>
      )}
      {message && <p role="status">{message}</p>}
      <EncounterSharing key={scope} organizationId={organizationId} encounterId={encounterId} />
      <details>
        <summary>Saved reports ({reports.length})</summary>
        <ul className="flex flex-col gap-3 pt-3">
          {reports.map((r) => (
            <li key={r.document_id} className="rounded border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span>
                  {r.title} · {r.signed_version_id ? 'Signed' : 'Draft'}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      setDocument(
                        (await clientApi.get(`/api/v1/organizations/${organizationId}/documents/${r.document_id}`))
                          .data,
                      );
                    })
                  }>
                  Open report
                </Button>
                {r.signed_version_id && can('export_document') && (
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy}
                    onClick={() =>
                      void run(async () => {
                        const result = await clientApi.get(
                          `${base}/reports/${r.document_id}/signed/${r.signed_version_id}/pdf`,
                          { responseType: 'blob' },
                        );
                        downloadClinicalFile(result.data, 'application/pdf', 'signed-clinical-report.pdf');
                      })
                    }>
                    Signed PDF
                  </Button>
                )}
              </div>
              {r.kind === 'BIRTH_SUMMARY' && r.signed_version_id && can('export_document') && (
                <WorksheetActions report={r} base={base} configuration={configuration} />
              )}
            </li>
          ))}
        </ul>
      </details>
      <DocumentViewerSheet
        open={!!document}
        document={document}
        onOpenChange={(open) => {
          if (!open) {
            setDocument(null);
            void clientApi
              .get(`${base}/episodes/${episode.id}/reports`)
              .then((r) => setReports(r.data))
              .catch(() => {});
          }
        }}
      />
    </section>
  );
}
function EncounterSharing({ organizationId, encounterId }: { organizationId: string; encounterId: string }) {
  const { can } = usePermissions();
  const [value, setValue] = useState<{ private: boolean; prenatal_sharing_enabled: boolean } | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const url = `/api/v1/organizations/${organizationId}/document-review/encounters/${encounterId}/visibility`;
  useEffect(() => {
    let current = true;
    void clientApi
      .get(url)
      .then((r) => {
        if (current) setValue(r.data);
      })
      .catch(() => {
        if (current) setMessage('Sharing preferences could not be loaded.');
      });
    return () => {
      current = false;
    };
  }, [url]);
  return (
    <details>
      <summary>Encounter privacy and prenatal sharing</summary>
      <p className="text-sm">
        Sharing remains off until explicitly enabled for every source encounter. Private encounters cannot be shared.
        This control does not send or publish a report.
      </p>
      {value && (
        <fieldset disabled={busy || !can('update_encounter')} className="flex flex-col gap-2 py-2">
          <label className="flex gap-2">
            <input
              type="checkbox"
              checked={value.private}
              onChange={(e) =>
                setValue({
                  ...value,
                  private: e.target.checked,
                  prenatal_sharing_enabled: e.target.checked ? false : value.prenatal_sharing_enabled,
                })
              }
            />
            Private encounter
          </label>
          <label className="flex gap-2">
            <input
              type="checkbox"
              checked={value.prenatal_sharing_enabled}
              disabled={value.private}
              onChange={(e) => setValue({ ...value, prenatal_sharing_enabled: e.target.checked })}
            />
            Allow this encounter in signed prenatal report sharing
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setBusy(true);
              void clientApi
                .put(url, value)
                .then(() => setMessage('Sharing preferences saved.'))
                .catch(() => setMessage('Could not save preferences.'))
                .finally(() => setBusy(false));
            }}>
            Save sharing preferences
          </Button>
        </fieldset>
      )}
      {message && <p role="status">{message}</p>}
    </details>
  );
}
function WorksheetActions({
  report,
  base,
  configuration,
}: {
  report: SavedReport;
  base: string;
  configuration?: PracticeConfiguration | null;
}) {
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<{ notice: string; fields: { label: string; value: unknown }[] } | null>(null);
  const request = useRef(0);
  useEffect(() => {
    request.current++;
    setResult(null);
    setMessage('');
    return () => {
      request.current++;
    };
  }, [report.document_id, report.signed_version_id]);
  const mappings = configuration?.clinically_reviewed
    ? (configuration.state_worksheets?.filter((m) => m.clinically_reviewed) ?? [])
    : [];
  return (
    <details className="mt-3">
      <summary>Birth certificate preparation worksheet</summary>
      <p>
        Available only from this signed summary and a reviewed jurisdiction mapping. This does not file a certificate.
      </p>
      {!mappings.length && <p>No reviewed mappings configured.</p>}
      {mappings.flatMap((m) =>
        report.babies.map((b) => (
          <Button
            key={`${m.id}/${b.fetus_id}`}
            type="button"
            variant="outline"
            onClick={() => {
              const current = ++request.current;
              setResult(null);
              setMessage('');
              void clientApi
                .get(`${base}/reports/${report.document_id}/signed/${report.signed_version_id}/worksheet`, {
                  params: { mapping_id: m.id, fetus_id: b.fetus_id },
                })
                .then((r) => {
                  if (current === request.current) setResult(r.data);
                })
                .catch((e) => {
                  if (current === request.current) setMessage(errorText(e));
                });
            }}>
            {m.jurisdiction} · {m.title} · {b.label}
          </Button>
        )),
      )}
      {result && (
        <div>
          <p>{result.notice}</p>
          <dl>
            {result.fields.map((f, i) => (
              <div key={i} className="grid grid-cols-2 gap-4 border-b py-2">
                <dt>{f.label}</dt>
                <dd>{f.value === null || f.value === undefined ? 'Not recorded' : String(f.value)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
      {message && <p role="status">{message}</p>}
    </details>
  );
}
